import React, { ComponentProps, ReactNode, forwardRef } from 'react'
import cn from 'clsx'
import Clickable from 'components/Common/Clickable'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from './Typography/BodyText'
import Caption from './Typography/Caption'
import { SVG_ICON_SIZE_CSS_VAR, StyledSvgIcon } from './SvgIcon'

const ReservedToggleButtonTitleSpace = styled(BodyText)`
  visibility: hidden;
  position: relative;
`

const ReservedToggleButtonSubtitleSpace = styled(Caption)`
  visibility: hidden;
  position: relative;
`

const ToggleButtonTitle = styled(BodyText)`
  visibility: visible;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const ToggleButtonSubtitle = styled(Caption)`
  visibility: visible;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const ClickableToggleButton = styled(Clickable)`
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: ${props => props.theme.borderRadius.large};
  transition: .2s background-color;

  > ${StyledSvgIcon} {
    ${SVG_ICON_SIZE_CSS_VAR}: ${rem(24)};
  }

  > ${StyledSvgIcon} + ${ReservedToggleButtonTitleSpace} {
    margin-top: ${rem(4)};
  }

  &.density-comfortable {
    padding: ${rem(12)} ${rem(20)};
  }
  &.density-compact {
    padding: ${rem(8)} ${rem(20)};
  }

  &.is-selected {
    background-color: ${props => props.theme.palette.neutral.six};
  }

  &:hover:not(:disabled, .is-selected) {
    background-color: ${props => props.theme.palette.neutral.six};
  }
`

interface Props extends Omit<ComponentProps<typeof Clickable>, 'title'> {
  /** puts the toggle button in the selected state */
  isSelected?: boolean
  /** sets the textual content of the toggle button's title */
  title: ReactNode
  /** sets the typography format of the title */
  titleFormat?: ComponentProps<typeof BodyText>['format']
  /** sets the textual content of the subtitle sitting underneath the title */
  subtitle?: string
  /** sets the typography format of the subtitle */
  subtitleFormat?: ComponentProps<typeof Caption>['format']
  /**
   * sets the icon sitting on top of the title
   *
   * there's no need to provide the icon size
   *  */
  icon?: ReactNode
  /**
   * sets the padding of the button
   *
   * @default 'comfortable'
   */
  density?: 'comfortable' | 'compact'
  className?: string
  id?: string
}

const ToggleButton = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    isSelected,
    title,
    titleFormat,
    subtitle,
    subtitleFormat,
    icon,
    density = 'comfortable',
    id,
    className,
    ...rest
  } = props

  return <ClickableToggleButton
    {...rest}
    ref={ref}
    id={id}
    className={cn(className, `density-${density}`, {
      'is-selected': isSelected,
    })}
  >
    {icon}
    <ReservedToggleButtonTitleSpace
      variant="medium"
      colour="neutral-two"
      weight="bold"
      align="center"
      wrap="no-wrap"
      format={titleFormat}
    >
      {title}
      <ToggleButtonTitle
        variant="medium"
        colour="neutral-two"
        weight={isSelected ? 'bold' : 'normal'}
        align="center"
        format={titleFormat}
      >
        {title}
      </ToggleButtonTitle>
    </ReservedToggleButtonTitleSpace>
    {subtitle && <ReservedToggleButtonSubtitleSpace
      variant="medium"
      colour="neutral-four"
      weight="bold"
      align="center"
      wrap="no-wrap"
      format={subtitleFormat}
    >
      {subtitle}
      <ToggleButtonSubtitle
        variant="medium"
        colour="neutral-four"
        weight={isSelected ? 'bold' : 'normal'}
        align="center"
        format={subtitleFormat}
      >
        {subtitle}
      </ToggleButtonSubtitle>
    </ReservedToggleButtonSubtitleSpace>}
  </ClickableToggleButton>
})

export default ToggleButton
